import { useCallback, useEffect, useRef } from "react";

import styled from "@emotion/styled";
import throttle from "lodash/throttle";

import blob5 from "@/../../hypetrain/static/img/blob5.svg";
import blob6 from "@/../../hypetrain/static/img/blob6.svg";
import blob11 from "@/../../hypetrain/static/img/blob11.svg";
import celebrateVid from "@/../../hypetrain/static/img/celebrate.webp";
import celebrateFrame from "@/../../hypetrain/static/img/celebrate-frame.jpg";
import confetti from "@/../../hypetrain/static/img/confetti.png";
import eileenChatVid from "@/../../hypetrain/static/img/eileen-chat.webp";
import eileenChatFrame from "@/../../hypetrain/static/img/eileen-chat-frame.jpg";
import nameCard from "@/../../hypetrain/static/img/name-card.png";
import phoneFrame from "@/../../hypetrain/static/img/phone-frame.jpg";
import resume from "@/../../hypetrain/static/img/resume.png";
import teamUp from "@/../../hypetrain/static/img/team-up.png";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { LazyImg } from "@/components/helpers/ui/LazyImg";
import { LazyImgWithFallback } from "@/components/helpers/ui/LazyImgWithFallback";
import { Box, Flex } from "@/components/layout/flexbox";
import { breakpoints, Media } from "@/components/layout/Media";
import { Container } from "@/components/pieces/bootstrap";
import { MIDTEXT_COLOR } from "@/css/constants";

const ITEM_HEIGHT = 500;
const ITEM_MOBILE_HEIGHT = 200;
const STICKY_TOP = 200;

const BENEFITS = [
  {
    key: "teamUp",
    title: "Team up with a career coach",
    description: "Choose an expert coach who cares about your success",
    color: "#6466EB",
    gradient:
      "linear-gradient(0deg, rgba(181,70,146,1) 0%, rgba(100,102,235,1) 100%)",
    graphic: (
      <Box maxHeight={ITEM_HEIGHT}>
        <EmbedSvg src={blob11} width="100%" />
        <Media at="xs">
          <LazyImg
            width="100%"
            src={phoneFrame}
            style={{ position: "absolute", top: "0", left: "0" }}
            className="round-corners"
          />
        </Media>
        <Media greaterThan="xs">
          <LazyImgWithFallback
            width="100%"
            src={teamUp}
            fallback={phoneFrame}
            alt="Team Up"
            height="auto"
            style={{ position: "absolute", top: "0", left: "0" }}
            className="round-corners"
          />
        </Media>
        <LazyImg
          width="42%"
          src={nameCard}
          style={{ position: "absolute", bottom: "5%", left: "27%" }}
          className="round-corners"
        />
      </Box>
    ),
  },
  {
    key: "workToward",
    title: "Work toward your goals",
    description:
      "You will Zoom with your coach to align on your goals, craft an action plan, and work together to make it happen.",
    color: "#B54692",
    gradient:
      "linear-gradient(0deg, rgba(244,178,73,1) 0%, rgba(181,70,146,1) 100%)",
    graphic: (
      <Box maxHeight={ITEM_HEIGHT} position="relative">
        <EmbedSvg src={blob5} width="100%" />
        <Media at="xs">
          <LazyImg
            width="67.7%"
            src={eileenChatFrame}
            style={{
              position: "absolute",
              top: "23.5%",
              left: "7.578%",
              borderBottomRightRadius: 10,
              borderBottomLeftRadius: 10,
            }}
          />
        </Media>
        <Media greaterThan="xs">
          <LazyImgWithFallback
            width="67.5%"
            src={eileenChatVid}
            fallback={eileenChatFrame}
            alt="Eileen Chat"
            height="auto"
            style={{
              position: "absolute",
              top: "23.5%",
              left: "7.578%",
              borderBottomRightRadius: 10,
              borderBottomLeftRadius: 10,
            }}
          />
        </Media>
        <LazyImg
          width="90.6%"
          src={resume}
          style={{ position: "absolute", top: "13.5%", left: "7.577%" }}
        />
      </Box>
    ),
  },
  {
    key: "upgradeYourLife",
    title: "Upgrade your life at work",
    description:
      "Land your dream job. Get the promotion. Change industries. With your coach's help, you can achieve your goals.",
    color: "#F4B249",
    gradient: "none",
    graphic: (
      <Box maxHeight={ITEM_HEIGHT} position="relative">
        <EmbedSvg src={blob6} width="100%" />
        <Media at="xs">
          <LazyImg
            width="76.3%"
            src={celebrateFrame}
            style={{ position: "absolute", top: "18.3%", left: "11.9%" }}
            className="round-corners"
          />
        </Media>
        <Media greaterThan="xs">
          <LazyImgWithFallback
            width="76.3%"
            src={celebrateVid}
            fallback={celebrateFrame}
            alt="Celebrate"
            height="auto"
            style={{
              position: "absolute",
              minHeight: 200,
              top: "18.3%",
              left: "11.9%",
            }}
            className="round-corners"
          />
        </Media>
        <LazyImg
          width="33.9%"
          src={confetti}
          style={{ position: "absolute", top: "7.8%", right: "13.6%" }}
        />
      </Box>
    ),
  },
];

export function ProBenefits() {
  const ref = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    BENEFITS.forEach((item) => {
      const numberSelector = document.querySelector(`.${item.key}-step-number`);
      const top =
        (numberSelector ? numberSelector.getBoundingClientRect().top : 0) -
        ITEM_HEIGHT;
      const height = top < 0 ? -top : 0;
      const lineSelector = document.querySelector(`.${item.key}-process-line`);
      if (lineSelector) {
        (lineSelector as HTMLElement).style.setProperty(
          "height",
          `${height}px`,
        );
      }
    });
    BENEFITS.forEach((item, index) => {
      const lineSelector = document.querySelector(`.${item.key}-process-line`);
      const lineHeight = lineSelector
        ? lineSelector.getBoundingClientRect().height
        : 0;
      const nextLineSelector =
        BENEFITS[index + 1] &&
        document.querySelector(`.${BENEFITS[index + 1].key}-process-line`);
      const nextLineHeight = nextLineSelector
        ? nextLineSelector.getBoundingClientRect().height
        : 0;

      const selector = document.querySelector(`.${item.key}`);
      if (selector instanceof HTMLElement) {
        const visibility =
          (lineHeight > 0 && nextLineHeight == 0) ||
          (lineHeight == 0 && nextLineHeight == 0 && index == 0)
            ? { visibility: "visible", opacity: "1" }
            : { visibility: "hidden", opacity: "0" };

        Object.entries(visibility).forEach(([property, value]) => {
          selector.style.setProperty(property, value);
        });
      }
    });
  };

  useEffect(() => {
    const scrollHandler = throttle(handleScroll, 20);
    window.addEventListener("scroll", scrollHandler);

    return () => window.removeEventListener("scroll", scrollHandler);
  }, []);

  const setRefs = useCallback((node: HTMLDivElement) => {
    ref.current = node;
  }, []);

  return (
    <>
      <Box backgroundColor={"#FBFBFF"} pt={20}>
        <Container>
          <Flex m={"0 auto"} justifyContent="center" pb={3} ref={setRefs}>
            <Box width={[0, 1 / 3]}></Box>
            <Flex
              width={[1, 1 / 2]}
              ml={[0, 1 / 3]}
              pr={[0]}
              pl={[0, 3, 4, 6]}
              justifyContent="space-between"
              flexDirection="column"
            >
              <StyledMainTitle>HOW IT WORKS</StyledMainTitle>
              <Box fontSize={36} fontWeight="bold" mb={3}>
                Get set up in no time
              </Box>
            </Flex>
          </Flex>
          {BENEFITS.map((tool, index) => {
            const { key, title, description, graphic } = tool;
            const refHeight = ref.current?.offsetHeight || 0;
            const grids = [
              <Flex
                width={[1, 1 / 2]}
                pr={[0]}
                pl={[0, 3, 4, 6]}
                key={`${title}_description`}
              >
                <StepTimeLine>
                  <StepNumber className={`${key}-step-number`}>
                    {index + 1}
                  </StepNumber>
                  <StepTimeLineProcess
                    className={`${key}-process-line`}
                  ></StepTimeLineProcess>
                </StepTimeLine>
                <Box mb={2}>
                  <StyledTitle>{title}</StyledTitle>
                  <Box color={MIDTEXT_COLOR} mt={3}>
                    <StyledDescription>{description}</StyledDescription>
                  </Box>
                </Box>
              </Flex>,
              <Box width={[1, 1 / 3]} key={`${title}_img`} mt={-refHeight}>
                <Media greaterThan="xs">
                  <StickyFade
                    key={`${title}_sticky`}
                    top={STICKY_TOP}
                    className={key}
                  >
                    <Box maxHeight={ITEM_HEIGHT} position="relative">
                      {graphic}
                    </Box>
                  </StickyFade>
                </Media>
              </Box>,
            ];

            return (
              <Flex
                alignItems="stretch"
                key={key}
                flexWrap="wrap"
                justifyContent="center"
                m={"0 auto"}
              >
                {grids.reverse()}
              </Flex>
            );
          })}
        </Container>
      </Box>
    </>
  );
}

function processColors() {
  let styles = "";

  BENEFITS.forEach((item) => {
    styles += `
    .${item.key}-step-number {
       background: ${item.color};
       box-shadow: 0px 0px 15px ${item.color}40;
    }
    .${item.key}-process-line {
      background-color: ${item.color};
      background: ${item.gradient};
    }
    `;
  });

  return `${styles}`;
}

const StepNumber = styled("div")`
  width: 48px;
  min-height: 48px;
  border-radius: 50%;
  font-weight: 600;
  text-align: center;
  font-size: 24px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StepTimeLineProcess = styled("div")`
  width: 3px;
  border-radius: 3px;
  margin-top: 3px;
`;

const StepTimeLine = styled("div")`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-right: 30px;
  min-height: ${ITEM_HEIGHT}px;
  max-height: ${ITEM_HEIGHT}px;
  ${processColors()};
  @media screen and (max-width: ${breakpoints.sm}px) {
    min-height: ${ITEM_MOBILE_HEIGHT}px;
    max-height: ${ITEM_MOBILE_HEIGHT}px;
  }
`;

const StickyFade = styled(Box)`
  position: sticky;
  top: ${(props) => props.top}px;
  transition:
    visibility 0.3s linear 0s,
    opacity 0.3s linear 0s;
`;

const StyledDescription = styled("span")`
  font-size: 22px;
  line-height: 1.5;
  @media screen and (max-width: ${breakpoints.sm}px) {
    font-size: 18px;
  }
`;

const StyledTitle = styled("h3")`
  line-height: 1.3;
  margin-top: 8px;
  font-size: 26px;
`;

const StyledMainTitle = styled("h2")`
  color: #6466eb;
  font-size: 20px;
`;
